import React, { useCallback, useEffect, useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileSpreadsheet, AlertCircle, CheckCircle2, SkipForward, Search, ChevronLeft, ChevronRight, Activity, ArrowUp, ArrowDown, ArrowUpDown, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export function HistoricalStatsPanel() {
  // Upload state
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadReport, setUploadReport] = useState(null);

  // Current season stats state
  const [currentStats, setCurrentStats] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [currentLoading, setCurrentLoading] = useState(false);
  const [currentViewMode, setCurrentViewMode] = useState("batting");
  const [currentSearch, setCurrentSearch] = useState("");
  const [currentGrades, setCurrentGrades] = useState([]);
  const [currentGradeFilter, setCurrentGradeFilter] = useState("all");
  const [currentMatchTypes, setCurrentMatchTypes] = useState([]);
  const [currentMatchTypeFilter, setCurrentMatchTypeFilter] = useState("all");
  const [currentTeamsByGrade, setCurrentTeamsByGrade] = useState({});
  const [currentTeamFilter, setCurrentTeamFilter] = useState("all");
  const [currentSortCol, setCurrentSortCol] = useState(null);
  const [currentSortDir, setCurrentSortDir] = useState("desc");
  const [currentSeasonOpen, setCurrentSeasonOpen] = useState(false);

  // List state
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [seasons, setSeasons] = useState([]);
  const [grades, setGrades] = useState([]);
  const [matchTypes, setMatchTypes] = useState([]);
  const [seasonFilter, setSeasonFilter] = useState("all");
  const [gradeFilter, setGradeFilter] = useState("all");
  const [matchTypeFilter, setMatchTypeFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const PAGE_SIZE = 50;

  // View mode: batting vs bowling vs fielding
  const [viewMode, setViewMode] = useState("batting");

  // Imported records sort state (server-side)
  const [importedSortCol, setImportedSortCol] = useState(null);
  const [importedSortDir, setImportedSortDir] = useState("desc");
  const [importedRecordsOpen, setImportedRecordsOpen] = useState(false);

  // Combined seasons state
  const [combinedOpen, setCombinedOpen] = useState(false);
  const [combinedItems, setCombinedItems] = useState([]);
  const [combinedTotal, setCombinedTotal] = useState(0);
  const [combinedLoading, setCombinedLoading] = useState(false);
  const [combinedViewMode, setCombinedViewMode] = useState("batting");
  const [combinedSearch, setCombinedSearch] = useState("");
  const [combinedSortCol, setCombinedSortCol] = useState(null);
  const [combinedSortDir, setCombinedSortDir] = useState("desc");
  const [combinedOffset, setCombinedOffset] = useState(0);
  const COMBINED_PAGE = 50;

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Totals state
  const [totals, setTotals] = useState(null);

  // Fetch list
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (seasonFilter && seasonFilter !== "all") params.season = seasonFilter;
      if (gradeFilter && gradeFilter !== "all") params.grade = gradeFilter;
      if (matchTypeFilter && matchTypeFilter !== "all") params.match_type = matchTypeFilter;
      if (searchQ.trim()) params.q = searchQ.trim();
      if (importedSortCol) { params.sort_by = importedSortCol; params.sort_dir = importedSortDir; }
      const totalsParams = {};
      if (seasonFilter && seasonFilter !== "all") totalsParams.season = seasonFilter;
      if (gradeFilter && gradeFilter !== "all") totalsParams.grade = gradeFilter;
      if (matchTypeFilter && matchTypeFilter !== "all") totalsParams.match_type = matchTypeFilter;
      if (searchQ.trim()) totalsParams.q = searchQ.trim();
      const [listRes, totalsRes] = await Promise.all([
        api.get("/stats/historical", { params }),
        api.get("/stats/historical/totals", { params: totalsParams }),
      ]);
      setItems(listRes.data.items || []);
      setTotal(listRes.data.total || 0);
      setSeasons(listRes.data.seasons || []);
      setGrades(listRes.data.grades || []);
      setMatchTypes(listRes.data.match_types || []);
      setTotals(totalsRes.data);
    } catch (e) {
      toast.error("Failed to load historical stats");
    }
    setLoading(false);
  }, [offset, seasonFilter, gradeFilter, matchTypeFilter, searchQ, importedSortCol, importedSortDir]);

  useEffect(() => {
    const t = setTimeout(loadList, 300);
    return () => clearTimeout(t);
  }, [loadList]);

  // Reset to page 0 when filter changes
  useEffect(() => { setOffset(0); }, [seasonFilter, gradeFilter, matchTypeFilter, searchQ]);

  // Reset team filter when grade changes
  useEffect(() => { setCurrentTeamFilter("all"); }, [currentGradeFilter]);

  // Fetch current season stats
  useEffect(() => {
    let cancelled = false;
    const loadCurrentStats = async () => {
      setCurrentLoading(true);
      try {
        const { data: summary } = await api.get("/public/stats/summary");
        if (cancelled) return;
        const seasonName = summary.season;
        setCurrentSeason(seasonName);
        if (seasonName) {
          // Fetch players, optionally filtered by grade and team
          const params = { season: seasonName };
          if (currentGradeFilter && currentGradeFilter !== "all") params.grade = currentGradeFilter;
          if (currentTeamFilter && currentTeamFilter !== "all") params.team = currentTeamFilter;
          const [playersRes, filtersRes] = await Promise.all([
            api.get("/stats/players", { params }),
            api.get("/stats/current-season-filters"),
          ]);
          if (cancelled) return;
          setCurrentStats(playersRes.data.players || []);
          // Set available grades, match types, and teams-by-grade
          setCurrentGrades(filtersRes.data.grades || []);
          setCurrentMatchTypes(filtersRes.data.match_types || []);
          setCurrentTeamsByGrade(filtersRes.data.teams_by_grade || {});
        } else {
          setCurrentStats([]);
        }
      } catch {
        if (!cancelled) setCurrentStats([]);
      }
      if (!cancelled) setCurrentLoading(false);
    };
    loadCurrentStats();
    return () => { cancelled = true; };
  }, [currentGradeFilter, currentTeamFilter]);

  // Download template
  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/stats/historical/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "historical_stats_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch {
      toast.error("Failed to download template");
    }
  };

  // Upload CSV
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please select a .csv file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10 MB)");
      return;
    }

    setUploading(true);
    setUploadReport(null);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await api.post("/stats/historical/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadReport(data);
      if (data.imported > 0) {
        toast.success(`Imported ${data.imported} record${data.imported === 1 ? "" : "s"}`);
      } else if (data.skipped_duplicates > 0) {
        toast.info("All rows were duplicates — nothing new imported");
      } else {
        toast.warning("No records imported");
      }
      loadList();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Upload failed";
      toast.error(detail);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  // Delete record
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await api.delete(`/stats/historical/${deleteConfirm.id}`);
      toast.success(`Deleted ${deleteConfirm.player_name} (${deleteConfirm.season})`);
      setDeleteConfirm(null);
      loadList();
    } catch {
      toast.error("Delete failed");
    }
    setDeleting(false);
  };

  const hasNext = offset + PAGE_SIZE < total;
  const hasPrev = offset > 0;
  const pageNum = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Combined pagination helpers
  const combinedHasNext = combinedOffset + COMBINED_PAGE < combinedTotal;
  const combinedHasPrev = combinedOffset > 0;
  const combinedPageNum = Math.floor(combinedOffset / COMBINED_PAGE) + 1;
  const combinedTotalPages = Math.max(1, Math.ceil(combinedTotal / COMBINED_PAGE));

  // Fetch combined total on mount (count only, no full data)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/stats/combined", { params: { limit: 1, offset: 0 } });
        if (!cancelled) setCombinedTotal(data.total || 0);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch combined stats when card is open
  useEffect(() => {
    if (!combinedOpen) return;
    let cancelled = false;
    const loadCombined = async () => {
      setCombinedLoading(true);
      try {
        const params = { limit: COMBINED_PAGE, offset: combinedOffset };
        if (combinedSearch.trim()) params.q = combinedSearch.trim();
        if (combinedSortCol) { params.sort_by = combinedSortCol; params.sort_dir = combinedSortDir; }
        const { data } = await api.get("/stats/combined", { params });
        if (!cancelled) {
          setCombinedItems(data.items || []);
          setCombinedTotal(data.total || 0);
        }
      } catch {
        if (!cancelled) { setCombinedItems([]); setCombinedTotal(0); }
      }
      if (!cancelled) setCombinedLoading(false);
    };
    loadCombined();
    return () => { cancelled = true; };
  }, [combinedOpen, combinedOffset, combinedSearch, combinedSortCol, combinedSortDir]);

  // Reset combined offset on search/sort change
  useEffect(() => { setCombinedOffset(0); }, [combinedSearch, combinedSortCol, combinedSortDir]);

  const v = (val) => val != null && val !== "" ? val : "—";

  // ── Sort helpers ──
  const toggleCurrentSort = (col) => {
    if (currentSortCol === col) {
      setCurrentSortDir(d => d === "desc" ? "asc" : "desc");
    } else {
      setCurrentSortCol(col);
      setCurrentSortDir("desc");
    }
  };
  const toggleImportedSort = (col) => {
    if (importedSortCol === col) {
      setImportedSortDir(d => d === "desc" ? "asc" : "desc");
    } else {
      setImportedSortCol(col);
      setImportedSortDir("desc");
    }
    setOffset(0);
  };
  const toggleCombinedSort = (col) => {
    if (combinedSortCol === col) {
      setCombinedSortDir(d => d === "desc" ? "asc" : "desc");
    } else {
      setCombinedSortCol(col);
      setCombinedSortDir("desc");
    }
  };
  const SortIcon = ({ col, activeCol, activeDir }) => {
    if (col !== activeCol) return <ArrowUpDown className="h-3 w-3 ml-0.5 opacity-30 inline" />;
    return activeDir === "desc"
      ? <ArrowDown className="h-3 w-3 ml-0.5 inline text-foreground" />
      : <ArrowUp className="h-3 w-3 ml-0.5 inline text-foreground" />;
  };
  const SortHead = ({ label, col, activeCol, activeDir, onToggle, className = "" }) => (
    <TableHead
      className={`whitespace-nowrap text-right cursor-pointer select-none hover:text-foreground ${className}`}
      onClick={() => onToggle(col)}
    >
      {label}<SortIcon col={col} activeCol={activeCol} activeDir={activeDir} />
    </TableHead>
  );

  // Client-side sort for current season
  const sortedCurrentStats = React.useMemo(() => {
    const filtered = currentStats.filter(p =>
      !currentSearch.trim() || p.player_name.toLowerCase().includes(currentSearch.trim().toLowerCase())
    );
    if (!currentSortCol) return filtered;
    const dir = currentSortDir === "desc" ? -1 : 1;
    return [...filtered].sort((a, b) => {
      let av = a[currentSortCol] ?? 0;
      let bv = b[currentSortCol] ?? 0;
      if (typeof av === "string") { av = parseFloat(av) || 0; }
      if (typeof bv === "string") { bv = parseFloat(bv) || 0; }
      return (av - bv) * dir;
    });
  }, [currentStats, currentSearch, currentSortCol, currentSortDir]);

  return (
    <div className="space-y-4">
      {/* Upload section */}
      <Card className="p-4 rounded-2xl" data-testid="historical-stats-upload-card">
        <div className="flex items-center gap-2.5 mb-3">
          <FileSpreadsheet className="h-4 w-4 text-[hsl(var(--accent))]" />
          <div className="text-sm font-semibold">Import Historical Stats</div>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Upload a CSV exported from your stats spreadsheet. The importer automatically recognises common column names (e.g. "Matches played", "Batting Average"). Duplicate rows (same player + season + grade + match type) will be skipped.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full gap-1.5"
            onClick={downloadTemplate}
            data-testid="historical-stats-template"
          >
            <Download className="h-3.5 w-3.5" /> Download Template
          </Button>

          <div className="relative">
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              onChange={handleUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploading}
              data-testid="historical-stats-file-input"
            />
            <Button
              size="sm"
              className="rounded-full gap-1.5 bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))] hover:bg-[hsl(var(--primary))]/90"
              disabled={uploading}
              data-testid="historical-stats-upload"
            >
              <Upload className="h-3.5 w-3.5" />
              {uploading ? "Uploading…" : "Upload CSV"}
            </Button>
          </div>
        </div>

        {/* Upload report */}
        {uploadReport && (
          <div className="mt-4 rounded-xl border border-border bg-muted/20 p-3 space-y-2" data-testid="historical-stats-report">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Import Report</div>
            <div className="flex flex-wrap gap-3 text-sm">
              {uploadReport.imported > 0 && (
                <span className="inline-flex items-center gap-1 text-[hsl(142_42%_34%)]" data-testid="historical-stats-report-imported">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {uploadReport.imported} imported
                </span>
              )}
              {uploadReport.skipped_duplicates > 0 && (
                <span className="inline-flex items-center gap-1 text-[hsl(38_92%_45%)]" data-testid="historical-stats-report-skipped">
                  <SkipForward className="h-3.5 w-3.5" /> {uploadReport.skipped_duplicates} skipped (duplicates)
                </span>
              )}
              {uploadReport.errors && uploadReport.errors.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[hsl(6_72%_46%)]" data-testid="historical-stats-report-errors">
                  <AlertCircle className="h-3.5 w-3.5" /> {uploadReport.errors.length} warning{uploadReport.errors.length === 1 ? "" : "s"}
                </span>
              )}
            </div>

            {uploadReport.errors && uploadReport.errors.length > 0 && (
              <ScrollArea className="max-h-40 rounded-lg border border-border bg-background p-2">
                <div className="space-y-1">
                  {uploadReport.errors.map((err, i) => (
                    <div key={i} className="text-[11px] text-muted-foreground font-mono">
                      {err}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        )}
      </Card>

      {/* Current Season Stats */}
      <Collapsible open={currentSeasonOpen} onOpenChange={setCurrentSeasonOpen}>
        <Card className="p-4 rounded-2xl" data-testid="current-season-stats-card">
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between cursor-pointer select-none" data-testid="current-season-stats-trigger">
              <div className="flex items-center gap-2.5">
                <Activity className="h-4 w-4 text-[hsl(var(--accent))]" />
                <div className="text-sm font-semibold">
                  Current Season Stats{currentSeason ? ` — ${currentSeason}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="rounded-full px-2 py-0 text-[11px]" data-testid="current-season-stats-count">
                  {(currentSearch.trim() ? currentStats.filter(p => p.player_name.toLowerCase().includes(currentSearch.trim().toLowerCase())).length : currentStats.length)} player{currentStats.length === 1 ? "" : "s"}
                </Badge>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${currentSeasonOpen ? "rotate-180" : ""}`} />
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>

        {/* View mode tabs + filters */}
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <Tabs value={currentViewMode} onValueChange={setCurrentViewMode}>
            <TabsList className="h-8" data-testid="current-season-view-tabs">
              <TabsTrigger value="batting" className="text-xs h-7 px-3" data-testid="current-season-tab-batting">Batting</TabsTrigger>
              <TabsTrigger value="bowling" className="text-xs h-7 px-3" data-testid="current-season-tab-bowling">Bowling</TabsTrigger>
              <TabsTrigger value="fielding" className="text-xs h-7 px-3" data-testid="current-season-tab-fielding">Fielding</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex flex-wrap items-end gap-3 ml-auto">
            <div>
              <Label className="text-xs">Grade</Label>
              <Select value={currentGradeFilter} onValueChange={setCurrentGradeFilter}>
                <SelectTrigger className="h-8 w-44 rounded-lg text-xs mt-1" data-testid="current-season-grade-filter">
                  <SelectValue placeholder="All grades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All grades</SelectItem>
                  {currentGrades.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Team dropdown — visible when selected grade has multiple teams */}
            {(() => {
              const teamsForGrade = currentGradeFilter !== "all"
                ? (currentTeamsByGrade[currentGradeFilter] || [])
                : Object.values(currentTeamsByGrade).flat();
              if (teamsForGrade.length < 2) return null;
              const uniqueTeams = [...new Set(teamsForGrade)].sort();
              return (
                <div>
                  <Label className="text-xs">Team</Label>
                  <Select value={currentTeamFilter} onValueChange={setCurrentTeamFilter}>
                    <SelectTrigger className="h-8 w-52 rounded-lg text-xs mt-1" data-testid="current-season-team-filter">
                      <SelectValue placeholder="All teams" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All teams</SelectItem>
                      {uniqueTeams.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}

            <div>
              <Label className="text-xs">Match Type</Label>
              <Select value={currentMatchTypeFilter} onValueChange={setCurrentMatchTypeFilter}>
                <SelectTrigger className="h-8 w-44 rounded-lg text-xs mt-1" data-testid="current-season-matchtype-filter">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {currentMatchTypes.map((mt) => (
                    <SelectItem key={mt} value={mt}>{mt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Player name</Label>
              <div className="relative mt-1">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={currentSearch}
                  onChange={(e) => setCurrentSearch(e.target.value)}
                  placeholder="Search player…"
                  className="h-8 w-48 pl-8 text-xs rounded-lg"
                  data-testid="current-season-search"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap sticky left-0 bg-muted/50 z-10">Player</TableHead>

                {currentViewMode === "batting" && (
                  <>
                    <SortHead label="Mat" col="matches" activeCol={currentSortCol} activeDir={currentSortDir} onToggle={toggleCurrentSort} />
                    <SortHead label="Inn" col="innings_batted" activeCol={currentSortCol} activeDir={currentSortDir} onToggle={toggleCurrentSort} />
                    <SortHead label="Runs" col="runs" activeCol={currentSortCol} activeDir={currentSortDir} onToggle={toggleCurrentSort} />
                    <SortHead label="Avg" col="batting_average" activeCol={currentSortCol} activeDir={currentSortDir} onToggle={toggleCurrentSort} />
                    <SortHead label="SR" col="strike_rate" activeCol={currentSortCol} activeDir={currentSortDir} onToggle={toggleCurrentSort} />
                    <SortHead label="HS" col="highest" activeCol={currentSortCol} activeDir={currentSortDir} onToggle={toggleCurrentSort} />
                    <SortHead label="NO" col="not_outs" activeCol={currentSortCol} activeDir={currentSortDir} onToggle={toggleCurrentSort} />
                    <SortHead label="100s" col="centuries" activeCol={currentSortCol} activeDir={currentSortDir} onToggle={toggleCurrentSort} />
                    <SortHead label="50s" col="fifties" activeCol={currentSortCol} activeDir={currentSortDir} onToggle={toggleCurrentSort} />
                    <SortHead label="Ducks" col="ducks" activeCol={currentSortCol} activeDir={currentSortDir} onToggle={toggleCurrentSort} />
                    <SortHead label="4s" col="fours" activeCol={currentSortCol} activeDir={currentSortDir} onToggle={toggleCurrentSort} />
                    <SortHead label="6s" col="sixes" activeCol={currentSortCol} activeDir={currentSortDir} onToggle={toggleCurrentSort} />
                  </>
                )}

                {currentViewMode === "bowling" && (
                  <>
                    <TableHead className="whitespace-nowrap text-right">Overs</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Mdns</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Runs</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Wkts</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Avg</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Econ</TableHead>
                  </>
                )}

                {currentViewMode === "fielding" && (
                  <>
                    <TableHead className="whitespace-nowrap text-right">Catches</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Run Outs</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Stumpings</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentLoading && (
                <TableRow>
                  <TableCell colSpan={14} className="text-center text-muted-foreground py-8">Loading…</TableCell>
                </TableRow>
              )}
              {!currentLoading && currentStats.length === 0 && (
                <TableRow>
                  <TableCell colSpan={14} className="text-center text-muted-foreground py-8" data-testid="current-season-stats-empty">
                    {currentSeason ? "No scorecard data found for this season." : "No active season found."}
                  </TableCell>
                </TableRow>
              )}
              {!currentLoading && (() => {
                const sorted = currentViewMode === "batting"
                  ? (currentSortCol ? sortedCurrentStats : [...sortedCurrentStats].sort((a, b) => b.runs - a.runs))
                  : currentViewMode === "bowling"
                  ? sortedCurrentStats.filter(p => p.innings_bowled > 0).sort((a, b) => b.wickets - a.wickets)
                  : sortedCurrentStats.filter(p => (p.catches + p.run_outs + p.stumpings) > 0).sort((a, b) => (b.catches + b.run_outs + b.stumpings) - (a.catches + a.run_outs + a.stumpings));
                if (sorted.length === 0 && sortedCurrentStats.length === 0 && currentStats.length > 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={14} className="text-center text-muted-foreground py-8">No players match your search.</TableCell>
                    </TableRow>
                  );
                }
                return sorted.map((p, i) => (
                <TableRow key={p.player_id || p.player_name + i} data-testid={`current-season-row-${i}`}>
                  <TableCell className="font-medium whitespace-nowrap sticky left-0 bg-background z-10">{p.player_name}</TableCell>

                  {currentViewMode === "batting" && (
                    <>
                      <TableCell className="text-right tabular-nums">{p.matches}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.innings_batted}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{p.runs}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(p.batting_average)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(p.strike_rate)}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.highest}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.not_outs}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.centuries}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.fifties}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.ducks}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.fours}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.sixes}</TableCell>
                    </>
                  )}

                  {currentViewMode === "bowling" && (
                    <>
                      <TableCell className="text-right tabular-nums">{typeof p.overs === "number" ? p.overs.toFixed(1) : v(p.overs)}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.maidens}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.runs_conceded}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{p.wickets}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(p.bowling_average)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(p.economy)}</TableCell>
                    </>
                  )}

                  {currentViewMode === "fielding" && (
                    <>
                      <TableCell className="text-right tabular-nums">{p.catches}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.run_outs}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.stumpings}</TableCell>
                    </>
                  )}
                </TableRow>
              ));
              })()}
            </TableBody>
          </Table>
        </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Combined Seasons Stats */}
      <Collapsible open={combinedOpen} onOpenChange={setCombinedOpen}>
        <Card className="p-4 rounded-2xl" data-testid="combined-seasons-stats-card">
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between cursor-pointer select-none" data-testid="combined-seasons-trigger">
              <div className="flex items-center gap-2.5">
                <Activity className="h-4 w-4 text-[hsl(var(--accent))]" />
                <div className="text-sm font-semibold">Combined Seasons Stats</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="rounded-full px-2 py-0 text-[11px]" data-testid="combined-seasons-total">
                  {combinedTotal} player{combinedTotal === 1 ? "" : "s"}
                </Badge>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${combinedOpen ? "rotate-180" : ""}`} />
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-3">
              {/* Tabs + search */}
              <div className="flex flex-wrap items-end gap-3 mb-3">
                <Tabs value={combinedViewMode} onValueChange={setCombinedViewMode}>
                  <TabsList className="h-8" data-testid="combined-view-tabs">
                    <TabsTrigger value="batting" className="text-xs h-7 px-3" data-testid="combined-tab-batting">Batting</TabsTrigger>
                    <TabsTrigger value="bowling" className="text-xs h-7 px-3" data-testid="combined-tab-bowling">Bowling</TabsTrigger>
                    <TabsTrigger value="fielding" className="text-xs h-7 px-3" data-testid="combined-tab-fielding">Fielding</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="ml-auto">
                  <Label className="text-xs">Player name</Label>
                  <div className="relative mt-1">
                    <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={combinedSearch}
                      onChange={(e) => setCombinedSearch(e.target.value)}
                      placeholder="Search player…"
                      className="h-8 w-48 pl-8 text-xs rounded-lg"
                      data-testid="combined-search"
                    />
                  </div>
                </div>
              </div>

              {/* Table */}
              {combinedLoading ? (
                <div className="text-center py-8 text-muted-foreground text-sm">Loading…</div>
              ) : combinedItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">No data found</div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap sticky left-0 bg-muted/50 z-10">Player</TableHead>

                        {combinedViewMode === "batting" && (
                          <>
                            <SortHead label="Mat" col="matches" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="Inn" col="innings" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="Runs" col="runs" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="Avg" col="batting_average" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="HS" col="highest" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="NO" col="not_outs" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="100s" col="centuries" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="50s" col="fifties" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="Ducks" col="ducks" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="4s" col="fours" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="6s" col="sixes" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                          </>
                        )}

                        {combinedViewMode === "bowling" && (
                          <>
                            <SortHead label="Overs" col="overs" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="Mdns" col="maidens" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="Runs" col="runs_conceded" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="Wkts" col="wickets" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="Avg" col="bowling_average" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="Econ" col="economy" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="5W" col="five_w" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <TableHead className="whitespace-nowrap text-right">BB</TableHead>
                          </>
                        )}

                        {combinedViewMode === "fielding" && (
                          <>
                            <SortHead label="Catches" col="catches" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="Ct WK" col="catches_wk" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="Total Ct" col="total_catches" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="RO" col="run_outs" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="RO Ast" col="run_outs_assisted" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                            <SortHead label="St" col="stumpings" activeCol={combinedSortCol} activeDir={combinedSortDir} onToggle={toggleCombinedSort} />
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {combinedItems.map((p, i) => (
                        <TableRow key={p.player_key || i} data-testid={`combined-row-${i}`}>
                          <TableCell className="font-medium whitespace-nowrap sticky left-0 bg-background z-10">{p.player_name}</TableCell>

                          {combinedViewMode === "batting" && (
                            <>
                              <TableCell className="text-right tabular-nums">{p.matches}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.innings}</TableCell>
                              <TableCell className="text-right tabular-nums font-medium">{p.runs}</TableCell>
                              <TableCell className="text-right tabular-nums">{v(p.batting_average)}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.highest}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.not_outs}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.centuries}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.fifties}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.ducks}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.fours}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.sixes}</TableCell>
                            </>
                          )}

                          {combinedViewMode === "bowling" && (
                            <>
                              <TableCell className="text-right tabular-nums">{typeof p.overs === "number" ? p.overs.toFixed(1) : v(p.overs)}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.maidens}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.runs_conceded}</TableCell>
                              <TableCell className="text-right tabular-nums font-medium">{p.wickets}</TableCell>
                              <TableCell className="text-right tabular-nums">{v(p.bowling_average)}</TableCell>
                              <TableCell className="text-right tabular-nums">{v(p.economy)}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.five_w}</TableCell>
                              <TableCell className="text-right tabular-nums whitespace-nowrap">{p.best_bowling ? p.best_bowling.replace("--", "-") : "—"}</TableCell>
                            </>
                          )}

                          {combinedViewMode === "fielding" && (
                            <>
                              <TableCell className="text-right tabular-nums">{p.catches}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.catches_wk}</TableCell>
                              <TableCell className="text-right tabular-nums font-medium">{p.total_catches}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.run_outs}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.run_outs_assisted}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.stumpings}</TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Pagination */}
              {combinedTotal > COMBINED_PAGE && (
                <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                  <span>Showing {combinedOffset + 1}–{Math.min(combinedOffset + COMBINED_PAGE, combinedTotal)} of {combinedTotal}</span>
                  <div className="flex items-center gap-1.5">
                    <Button variant="outline" size="icon" className="h-6 w-6" disabled={!combinedHasPrev}
                      onClick={() => setCombinedOffset(o => Math.max(0, o - COMBINED_PAGE))} data-testid="combined-prev">
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="tabular-nums px-1">{combinedPageNum} / {combinedTotalPages}</span>
                    <Button variant="outline" size="icon" className="h-6 w-6" disabled={!combinedHasNext}
                      onClick={() => setCombinedOffset(o => o + COMBINED_PAGE)} data-testid="combined-next">
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Browse existing imports */}
      <Collapsible open={importedRecordsOpen} onOpenChange={setImportedRecordsOpen}>
        <Card className="p-4 rounded-2xl" data-testid="historical-stats-browse-card">
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between cursor-pointer select-none" data-testid="imported-records-trigger">
              <div className="text-sm font-semibold">Imported Records</div>
              <div className="flex items-center gap-2">
                {total > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    className="h-6 text-[11px] px-2 rounded-full"
                    onClick={(e) => { e.stopPropagation(); setDeleteAllConfirm(true); }}
                    data-testid="historical-stats-delete-all-btn"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />Delete All
                  </Button>
                )}
                <Badge variant="secondary" className="rounded-full px-2 py-0 text-[11px]" data-testid="historical-stats-total-badge">
                  {total}
                </Badge>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${importedRecordsOpen ? "rotate-180" : ""}`} />
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <div>
            <Label className="text-xs">Season</Label>
            <Select value={seasonFilter} onValueChange={setSeasonFilter}>
              <SelectTrigger className="h-9 mt-1 rounded-lg" data-testid="historical-stats-season-filter">
                <SelectValue placeholder="All seasons" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All seasons</SelectItem>
                {seasons.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Grade</Label>
            <Select value={gradeFilter} onValueChange={setGradeFilter}>
              <SelectTrigger className="h-9 mt-1 rounded-lg" data-testid="historical-stats-grade-filter">
                <SelectValue placeholder="All grades" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All grades</SelectItem>
                {grades.map((g) => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Match Type</Label>
            <Select value={matchTypeFilter} onValueChange={setMatchTypeFilter}>
              <SelectTrigger className="h-9 mt-1 rounded-lg" data-testid="historical-stats-matchtype-filter">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {matchTypes.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Player name</Label>
            <div className="relative mt-1">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search player…"
                className="h-9 pl-9 rounded-lg"
                data-testid="historical-stats-search"
              />
            </div>
          </div>
        </div>

        {/* View mode tabs */}
        <Tabs value={viewMode} onValueChange={setViewMode} className="mb-3">
          <TabsList className="h-8" data-testid="historical-stats-view-tabs">
            <TabsTrigger value="batting" className="text-xs h-7 px-3" data-testid="historical-stats-tab-batting">Batting</TabsTrigger>
            <TabsTrigger value="bowling" className="text-xs h-7 px-3" data-testid="historical-stats-tab-bowling">Bowling</TabsTrigger>
            <TabsTrigger value="fielding" className="text-xs h-7 px-3" data-testid="historical-stats-tab-fielding">Fielding</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Totals summary — only when a player is searched */}
        {totals && totals.total_records > 0 && searchQ.trim() && (
          <div data-testid="historical-stats-totals">
            {viewMode === "batting" && (
              <div className="grid grid-cols-6 sm:grid-cols-6 lg:grid-cols-12 gap-1.5 mb-3">
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_seasons.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Seasons</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_matches.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Mat</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_innings.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Inn</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_runs.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Runs</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_not_outs.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">NO</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{(totals.total_innings - totals.total_not_outs) > 0 ? (totals.total_runs / (totals.total_innings - totals.total_not_outs)).toFixed(2) : "—"}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Avg</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.highest_score ?? "—"}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">HS</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_100s.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">100s</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_50s.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">50s</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_4s.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">4s</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_6s.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">6s</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_ducks.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Ducks</div>
                </div>
              </div>
            )}
            {viewMode === "bowling" && (
              <div className="grid grid-cols-3 sm:grid-cols-7 gap-1.5 mb-3">
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_wickets.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Wkts</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums whitespace-nowrap">{totals.best_bowling ?? "—"}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">BB</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.bowling_average ?? "—"}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Avg</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.bowling_strike_rate ?? "—"}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">SR</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.bowling_economy ?? "—"}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Econ</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_5w.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">5W</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_overs_bowled != null ? Number(totals.total_overs_bowled).toFixed(2) : "—"}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Overs</div>
                </div>
              </div>
            )}
            {viewMode === "fielding" && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 mb-3">
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{totals.total_catches.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Total Catches</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{(totals.total_ct_wk ?? 0).toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Ct WK</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{(totals.total_run_outs ?? 0).toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">RO</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{(totals.total_run_out_assists ?? 0).toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">RO Ast</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold tabular-nums">{(totals.total_stumpings ?? 0).toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">St</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap sticky left-0 bg-muted/50 z-10">Player</TableHead>
                <TableHead className="whitespace-nowrap">Season</TableHead>
                <TableHead className="whitespace-nowrap">Grade</TableHead>
                <TableHead className="whitespace-nowrap">Type</TableHead>

                {viewMode === "batting" && (
                  <>
                    <SortHead label="Mat" col="matches_played" activeCol={importedSortCol} activeDir={importedSortDir} onToggle={toggleImportedSort} />
                    <SortHead label="Inn" col="innings" activeCol={importedSortCol} activeDir={importedSortDir} onToggle={toggleImportedSort} />
                    <SortHead label="Avg" col="batting_average" activeCol={importedSortCol} activeDir={importedSortDir} onToggle={toggleImportedSort} />
                    <SortHead label="Agg" col="batting_aggregate" activeCol={importedSortCol} activeDir={importedSortDir} onToggle={toggleImportedSort} />
                    <SortHead label="NO" col="not_outs" activeCol={importedSortCol} activeDir={importedSortDir} onToggle={toggleImportedSort} />
                    <SortHead label="50s" col="50s" activeCol={importedSortCol} activeDir={importedSortDir} onToggle={toggleImportedSort} />
                    <SortHead label="100s" col="100s" activeCol={importedSortCol} activeDir={importedSortDir} onToggle={toggleImportedSort} />
                    <SortHead label="Ducks" col="ducks" activeCol={importedSortCol} activeDir={importedSortDir} onToggle={toggleImportedSort} />
                    <SortHead label="HS" col="high_score" activeCol={importedSortCol} activeDir={importedSortDir} onToggle={toggleImportedSort} />
                    <SortHead label="4s" col="4s" activeCol={importedSortCol} activeDir={importedSortDir} onToggle={toggleImportedSort} />
                    <SortHead label="6s" col="6s" activeCol={importedSortCol} activeDir={importedSortDir} onToggle={toggleImportedSort} />
                  </>
                )}

                {viewMode === "bowling" && (
                  <>
                    <TableHead className="whitespace-nowrap text-right">Overs</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Balls</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Wkts</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Mdns</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Runs</TableHead>
                    <TableHead className="whitespace-nowrap text-right">5W</TableHead>
                    <TableHead className="whitespace-nowrap text-right">BB</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Avg</TableHead>
                    <TableHead className="whitespace-nowrap text-right">SR</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Econ</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Wd</TableHead>
                    <TableHead className="whitespace-nowrap text-right">NB</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Extras</TableHead>
                  </>
                )}

                {viewMode === "fielding" && (
                  <>
                    <TableHead className="whitespace-nowrap text-right">Catches</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Ct WK</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Total Ct</TableHead>
                    <TableHead className="whitespace-nowrap text-right">RO</TableHead>
                    <TableHead className="whitespace-nowrap text-right">RO Ast</TableHead>
                    <TableHead className="whitespace-nowrap text-right">St</TableHead>
                  </>
                )}

                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={20} className="text-center text-muted-foreground py-8">Loading…</TableCell>
                </TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={20} className="text-center text-muted-foreground py-8" data-testid="historical-stats-empty">
                    {(seasonFilter !== "all" || gradeFilter !== "all" || matchTypeFilter !== "all" || searchQ)
                      ? "No records match the current filters."
                      : "No imported historical stats yet. Upload a CSV above to get started."}
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.map((row) => (
                <TableRow key={row.id} data-testid={`historical-stats-row-${row.id}`}>
                  <TableCell className="font-medium whitespace-nowrap sticky left-0 bg-background z-10">{row.player_name}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Badge variant="outline" className="text-[10px] font-medium">{row.season}</Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{v(row.grade)}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{v(row.match_type)}</TableCell>

                  {viewMode === "batting" && (
                    <>
                      <TableCell className="text-right tabular-nums">{v(row.matches_played)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.innings)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.batting_average)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{v(row.batting_aggregate)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.not_outs)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row["50s"])}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row["100s"])}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.ducks)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.high_score)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row["4s"])}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row["6s"])}</TableCell>
                    </>
                  )}

                  {viewMode === "bowling" && (
                    <>
                      <TableCell className="text-right tabular-nums">{v(row.overs_bowled)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.balls_bowled)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{v(row.wickets)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.maidens)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.runs_scored)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row["5_wickets"])}</TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">{v(row.best_bowling)?.replace?.("--", "-") || v(row.best_bowling)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.bowling_average)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.strike_rate)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.economy_rate)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.wides)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.no_balls)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.total_extras)}</TableCell>
                    </>
                  )}

                  {viewMode === "fielding" && (
                    <>
                      <TableCell className="text-right tabular-nums">{v(row.catches)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.catches_wk)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{v(row.total_catches)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.run_outs)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.run_outs_assisted)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v(row.stumpings)}</TableCell>
                    </>
                  )}

                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                      onClick={() => setDeleteConfirm(row)}
                      data-testid={`historical-stats-delete-${row.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
            <span data-testid="historical-stats-page-info">
              Page {pageNum} of {totalPages} ({total} records)
            </span>
            <div className="flex gap-1.5">
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 rounded-lg"
                disabled={!hasPrev}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                data-testid="historical-stats-prev"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 rounded-lg"
                disabled={!hasNext}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
                data-testid="historical-stats-next"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}>
        <AlertDialogContent className="rounded-2xl" data-testid="historical-stats-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete historical stat record?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <strong>{deleteConfirm?.player_name}</strong>'s{" "}
              <strong>{deleteConfirm?.season}</strong>{deleteConfirm?.grade ? ` (${deleteConfirm.grade})` : ""} record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-fg))] hover:bg-[hsl(var(--destructive))]/90"
              data-testid="historical-stats-delete-confirm"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All confirmation */}
      <AlertDialog open={deleteAllConfirm} onOpenChange={(o) => { if (!o) setDeleteAllConfirm(false); }}>
        <AlertDialogContent className="rounded-2xl" data-testid="historical-stats-delete-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all imported records?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove all <strong>{total.toLocaleString()}</strong> imported historical stat records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingAll}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setDeletingAll(true);
                try {
                  await api.delete("/stats/historical/all");
                  setDeleteAllConfirm(false);
                  loadList();
                } catch {
                  // silently handle
                }
                setDeletingAll(false);
              }}
              disabled={deletingAll}
              className="bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-fg))] hover:bg-[hsl(var(--destructive))]/90"
              data-testid="historical-stats-delete-all-confirm"
            >
              {deletingAll ? "Deleting…" : "Delete All"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
